import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  House, Users, Ticket, WhatsappLogo, ChartBar, SignOut, List, X,
  CaretLeft, CaretRight, Storefront, UserCircleGear, ImageSquare, Lightning, ChartPieSlice,
} from "@phosphor-icons/react";

const KW_LOGO = "https://customer-assets.emergentagent.com/job_loyalty-onboard-kw/artifacts/7t06knss_WhatsApp%20Image%202026-04-01%20at%2014.43.05.jpeg";

const allNavItems = [
  { path: "/", icon: House, label: "Dashboard", roles: ["admin", "staff", "brand"] },
  { path: "/customers", icon: Users, label: "Customers", roles: ["admin", "staff", "brand"] },
  { path: "/vouchers", icon: Ticket, label: "Vouchers", roles: ["admin", "staff", "brand"] },
  { path: "/brands", icon: Storefront, label: "Brands & QR", roles: ["admin", "staff"] },
  { path: "/whatsapp", icon: WhatsappLogo, label: "WhatsApp Logs", roles: ["admin", "staff"] },
  { path: "/live-data", icon: Lightning, label: "Live API Data", roles: ["admin", "staff"] },
  { path: "/reports", icon: ChartBar, label: "Reports", roles: ["admin", "staff"] },
  { path: "/analytics", icon: ChartPieSlice, label: "Analytics", roles: ["admin", "staff"] },
  { path: "/users", icon: UserCircleGear, label: "User Management", roles: ["admin"] },
  { path: "/voucher-images", icon: ImageSquare, label: "Voucher Images", roles: ["admin"] },
];

export default function DashboardLayout({ children, onLogout, user }) {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const userRole = user?.role || "staff";
  const navItems = allNavItems.filter((item) => item.roles.includes(userRole));

  const roleBadgeColor = {
    admin: "bg-red-100 text-red-700",
    staff: "bg-blue-100 text-blue-700",
    brand: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-layout">
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />}

      <aside className={`fixed top-0 left-0 h-full bg-[#0A0A0A] z-50 transition-all duration-300 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 ${isCollapsed ? "lg:w-20" : "lg:w-64"}`} data-testid="sidebar">
        <div className={`flex items-center h-16 border-b border-white/10 ${isCollapsed ? "justify-center px-2" : "px-4"}`}>
          <img src={KW_LOGO} alt="KW Blue Pearl" className={`${isCollapsed ? "h-8" : "h-10"} w-auto rounded bg-white/90 p-1`} />
          {!isCollapsed && <span className="ml-2 font-heading text-sm font-semibold text-white leading-tight">KW Blue Pearl</span>}
          <button className="ml-auto lg:hidden text-white" onClick={() => setIsSidebarOpen(false)}><X size={24} /></button>
        </div>

        {/* User info */}
        {!isCollapsed && (
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-white text-sm font-medium truncate">{user?.name || user?.username}</p>
            <Badge className={`mt-1 text-[10px] ${roleBadgeColor[userRole] || roleBadgeColor.staff}`}>
              {userRole === "brand" ? user?.brand_name || "Brand" : userRole.toUpperCase()}
            </Badge>
          </div>
        )}

        <nav className="mt-4 px-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path === "/" && location.pathname === "/");
            return (
              <Link key={item.path} to={item.path} onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${isActive ? "bg-[#002FA7] text-white" : "text-[#94A3B8] hover:bg-white/10 hover:text-white"}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={22} weight={isActive ? "fill" : "regular"} />
                {!isCollapsed && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <button className="hidden lg:flex absolute bottom-20 left-1/2 -translate-x-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
          <Button variant="ghost" className={`w-full text-[#94A3B8] hover:text-white hover:bg-white/10 ${isCollapsed ? "justify-center" : "justify-start"}`} onClick={onLogout} data-testid="logout-btn">
            <SignOut size={22} />
            {!isCollapsed && <span className="ml-3">Logout</span>}
          </Button>
        </div>
      </aside>

      <div className={`transition-all duration-300 ${isCollapsed ? "lg:ml-20" : "lg:ml-64"}`}>
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-[#E2E8F0] flex items-center px-4 lg:px-8">
          <button className="lg:hidden p-2 -ml-2 mr-4 text-[#64748B] hover:text-[#0A0A0A]" onClick={() => setIsSidebarOpen(true)} data-testid="mobile-menu-btn">
            <List size={24} />
          </button>
          <h1 className="font-heading text-xl font-semibold text-[#0A0A0A]">
            {navItems.find((item) => item.path === location.pathname)?.label || "Dashboard"}
          </h1>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
